/**
 * @biomax/plugin-epi-validation — scientific-validation layer.
 *
 * Two halves:
 *   1. A verifier-first CORPUS of observed historical-outbreak series
 *      (real data fetched from cited open sources; see scripts/fetch-corpus.ts).
 *   2. A BACKTEST harness that scores the engine's projection against an
 *      observed series — MAE / RMSE / peak-timing / peak-size / final-size
 *      error, plus CRPS via the ensemble (reusing @biomax/plugin-epidemiology's
 *      continuousRankedProbabilityScore).
 *
 * The point: replace "trust the model" with "here is how it tracked COVID-2020
 * in Italy within X". No fabricated data — observed series carry a source +
 * retrieval date + (where available) a PMID that round-trips.
 *
 * Pure + deterministic; the corpus is injected (browser-safe) or loaded from
 * disk (loader-node.ts). kingdoms: '*'.
 */

import type { PluginManifest } from '@biomax/types';

// ---------------------------------------------------------------------------
// Corpus types
// ---------------------------------------------------------------------------

export interface ObservedPoint {
  /** ISO date (week-ending). */
  date: string;
  /** Cumulative confirmed cases as of this date. */
  cumulativeCases: number;
  /** New confirmed cases in the week ending this date (derived). */
  newCases: number;
}

export interface OutbreakSource {
  name: string;
  pmid?: string;
  url: string;
  /** ISO date the series was retrieved from the source. */
  retrievedAt: string;
}

export interface ReferenceOutbreak {
  id: string;
  /** Catalog pathogen id this outbreak is attributed to. */
  pathogenId: string;
  /** Catalog region id (for population denominator in the backtest). */
  regionId: string;
  label: string;
  /** Observation window; the wild-type/early-wave window is chosen where the
   *  catalog's single-strain parameters are most valid. */
  window: { from: string; to: string };
  /** Weekly observed series across the window. */
  series: ObservedPoint[];
  source: OutbreakSource;
  note: string;
}

// ---------------------------------------------------------------------------
// Backtest scoring (pure)
// ---------------------------------------------------------------------------

export interface BacktestScores {
  /** Mean absolute error of weekly new cases. */
  mae: number;
  /** Root-mean-square error of weekly new cases. */
  rmse: number;
  /** |peak-week(model) − peak-week(observed)| in weeks. */
  peakTimingErrorWeeks: number;
  /** (peak(model) − peak(observed)) / peak(observed). */
  peakSizeErrorFrac: number;
  /** (total(model) − total(observed)) / total(observed). */
  finalSizeErrorFrac: number;
  /** Number of aligned weeks scored. */
  weeks: number;
}

function argmax(a: readonly number[]): number {
  let bi = 0; for (let i = 1; i < a.length; i++) if (a[i]! > a[bi]!) bi = i; return bi;
}

/** Score a model weekly-incidence series against an observed one. Aligns to
 *  the shorter length; both are weekly new-case counts. */
export function scoreBacktest(observedWeekly: readonly number[], modelWeekly: readonly number[]): BacktestScores {
  const n = Math.min(observedWeekly.length, modelWeekly.length);
  if (n === 0) throw new Error('scoreBacktest: empty series');
  let absSum = 0, sqSum = 0, obsTot = 0, modTot = 0;
  for (let i = 0; i < n; i++) {
    const o = observedWeekly[i]!, m = modelWeekly[i]!;
    absSum += Math.abs(m - o); sqSum += (m - o) ** 2; obsTot += o; modTot += m;
  }
  const oPeakWk = argmax(observedWeekly.slice(0, n));
  const mPeakWk = argmax(modelWeekly.slice(0, n));
  const oPeak = observedWeekly[oPeakWk]!, mPeak = modelWeekly[mPeakWk]!;
  return {
    mae: absSum / n,
    rmse: Math.sqrt(sqSum / n),
    peakTimingErrorWeeks: Math.abs(mPeakWk - oPeakWk),
    peakSizeErrorFrac: oPeak > 0 ? (mPeak - oPeak) / oPeak : 0,
    finalSizeErrorFrac: obsTot > 0 ? (modTot - obsTot) / obsTot : 0,
    weeks: n,
  };
}

/** Cumulative → weekly-new differences (non-negative). */
export function cumulativeToWeeklyNew(cumulative: readonly number[]): number[] {
  const out: number[] = [];
  for (let i = 1; i < cumulative.length; i++) out.push(Math.max(0, cumulative[i]! - cumulative[i - 1]!));
  return out;
}

// ---------------------------------------------------------------------------
// Injectable corpus (browser-safe; loader-node.ts populates from disk)
// ---------------------------------------------------------------------------

const CORPUS = new Map<string, ReferenceOutbreak>();

export function __setOutbreakCorpus(outbreaks: readonly ReferenceOutbreak[]): void {
  CORPUS.clear();
  for (const o of outbreaks) CORPUS.set(o.id, o);
}
export function listOutbreaks(): ReferenceOutbreak[] { return [...CORPUS.values()]; }
export function getOutbreak(id: string): ReferenceOutbreak | undefined { return CORPUS.get(id); }

// ---------------------------------------------------------------------------
// Citations (round-tripped via @biomax/plugin-literature-fetch — verify:refs)
// ---------------------------------------------------------------------------

export const VALIDATION_REFERENCES = [
  {
    id: 'jhu-csse-2020',
    label: 'Dong, Du & Gardner 2020 Lancet Infect Dis — "An interactive web-based dashboard to track COVID-19 in real time" (JHU CSSE dataset)',
    pmid: '32087114',
    doi: '10.1016/s1473-3099(20)30120-1',
    appliesTo: 'COVID-2020 first-wave observed series (all covid-*-2020 outbreaks)',
    note: 'The JHU CSSE time-series is the observed source for every COVID-2020 first-wave reference (Italy + 20 further countries). Verified 2026-05-28.',
    verifiedAt: '2026-05-28',
  },
  {
    id: 'who-ert-2014',
    label: 'WHO Ebola Response Team (Aylward et al.) 2014 NEJM — "Ebola Virus Disease in West Africa — The First 9 Months of the Epidemic and Forward Projections"',
    pmid: '25244186',
    doi: '10.1056/nejmoa1411100',
    appliesTo: 'Ebola West Africa 2014-15 observed series (ebola-{gin,lbr,sle}-2014)',
    note: 'Peer-reviewed source for the WHO patient-database case counts (fetched via the C. Rivers compilation) used for the Ebola West Africa references. Verified 2026-05-28.',
    verifiedAt: '2026-05-28',
  },
  {
    id: 'globalhealth-mpox-2022',
    label: 'Kraemer et al. 2022 Lancet Infect Dis — "Tracking the 2022 monkeypox outbreak with epidemiological data in real-time" (Global.health / OWID dataset)',
    pmid: '35690074',
    doi: '10.1016/s1473-3099(22)00359-0',
    appliesTo: 'Mpox 2022 observed series (mpox-*-2022 outbreaks)',
    note: 'Peer-reviewed descriptor of the Global.health mpox line-list (served via Our World in Data) used as the observed source for the 2022 clade-IIb references. Verified 2026-05-28.',
    verifiedAt: '2026-05-28',
  },
] as const;

export const manifest: PluginManifest = {
  id: 'epi-validation', version: '0.1.0',
  engineCompat: '^0.5.0', kingdoms: '*', systems: [], components: [],
};
